import React, { useState, useEffect } from "react";

import { Box, useMediaQuery, useTheme } from "@mui/material";
import { Outlet } from "react-router";

import { AppBreadcrumbs } from "@shell/components/AppBreadcrumbs";
import {
  BreadcrumbProvider,
  useBreadcrumbContext,
} from "@shell/contexts/breadcrumbContext";

import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import { CONTENT_MAX_WIDTH, DRAWER_WIDTH } from "./constants";

/**
 * Inner layout that consumes the breadcrumb/content-width context.
 *
 * Split from {@link DashboardLayout} so the provider wraps both the `Topbar`
 * (which renders the breadcrumbs) and the routed `Outlet` (whose pages publish
 * breadcrumbs via {@link useBreadcrumbs}).
 */
const DashboardLayoutInner = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { breadcrumbs, fluidContent } = useBreadcrumbContext();

  // Automatically adjust sidebar based on screen size
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      if (isSmall) {
        setSidebarVisible(false);
      }
    } else {
      setSidebarVisible(true);
      setSidebarOpen(true); // Always open on larger screens
    }
  }, [isMobile, isSmall]);

  const toggleSidebar = () => {
    if (!sidebarVisible) {
      setSidebarVisible(true);
      // Small delay to ensure visibility before animation
      setTimeout(() => setSidebarOpen(true), 50);
    } else if (sidebarOpen) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  };

  const hideSidebar = () => {
    setSidebarOpen(false);
    // Wait for animation to complete before hiding
    setTimeout(() => setSidebarVisible(false), 300);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Skip link for keyboard navigation */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: -9999,
          top: 0,
          zIndex: 60,
          px: 2,
          py: 1,
          bgcolor: "background.paper",
          color: "primary.main",
          borderRadius: 1,
          fontWeight: 600,
          textDecoration: "none",
          "&:focus": {
            left: 16,
            top: 16,
          },
        }}
      >
        Skip to content
      </Box>

      {/* Topbar — full width, sticky. S1: breadcrumbs render in the center. */}
      <Topbar
        breadcrumbs={
          breadcrumbs.length > 0 ? (
            <AppBreadcrumbs items={breadcrumbs} compact />
          ) : null
        }
        onExpandSidebar={() => {
          if (!sidebarOpen && sidebarVisible) {
            setSidebarOpen(true);
          }
        }}
      />

      {/* Body: sidebar + main content side by side */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Sidebar component */}
        <Sidebar
          open={sidebarOpen}
          visible={sidebarVisible}
          width={DRAWER_WIDTH}
          isMobile={isMobile}
          onClose={hideSidebar}
          onToggle={toggleSidebar}
        />

        {/* Main content wrapper */}
        <Box
          component="div"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {/* Main content */}
          <Box
            component="main"
            id="main-content"
            sx={{
              flexGrow: 1,
              // S2: center content with a 1280px max-width on wide screens.
              // Pages can opt out via `useFluidContent(true)`.
              maxWidth: fluidContent ? "none" : `${CONTENT_MAX_WIDTH}px`,
              mx: "auto",
              width: "100%",
              // S17: 16px padding below md, 24px from md up.
              padding: { xs: "16px", md: "24px" },
              position: "relative",
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const DashboardLayout = () => (
  <BreadcrumbProvider>
    <DashboardLayoutInner />
  </BreadcrumbProvider>
);

export default DashboardLayout;
